import { useState, useEffect } from 'react';
import { Switch, useHistory, useRouteMatch } from 'react-router-dom';
import { useIdentity, usePersonalLoan } from 'shared/hooks';
import { PageRoute } from 'shared/components/PageRoute';
import { BasicInfoAndCreateAccount, StartApplicationContainerOld } from 'components';
import { FigureLogoAnimation } from '@figure/figure-frontlib-gui';

const useGetActivePersonalLoanApplication = () => {
  const { isAuthenticated } = useIdentity();
  const {
    personalLoan: existingActivePersonalLoanApplication,
    getActivePersonalLoan,
  } = usePersonalLoan(false);
  const hasExistingActivePersonalLoanApplication = !!existingActivePersonalLoanApplication.uuid;

  const [isInitialized, setIsInitialized] = useState(hasExistingActivePersonalLoanApplication);
  const [activePersonalLoanApplicationUuid, setActivePersonalLoanApplicationUuid] = useState();

  useEffect(() => {
    if (!isInitialized) {
      if (isAuthenticated) {
        getActivePersonalLoan()
          .then(({ uuid }) => {
            setActivePersonalLoanApplicationUuid(uuid);
          })
          .finally(() => {
            setIsInitialized(true);
          });
      } else {
        setIsInitialized(true);
      }
    }
  }, [
    getActivePersonalLoan,
    hasExistingActivePersonalLoanApplication,
    isAuthenticated,
    isInitialized,
  ]);

  return { isInitialized, activePersonalLoanApplicationUuid };
};

const useRedirectToNextStepOnceInitialized = () => {
  const history = useHistory();
  const match = useRouteMatch();
  const { isAuthenticated } = useIdentity();
  const {
    isInitialized,
    activePersonalLoanApplicationUuid,
  } = useGetActivePersonalLoanApplication();

  useEffect(() => {
    if (isInitialized) {
      history.push(`${match.path}/${activePersonalLoanApplicationUuid || '-'}/basic-info`);
    }
  }, [activePersonalLoanApplicationUuid, history, isAuthenticated, isInitialized, match.path]);
};

const RegisterRouterRedirectToCurrentStep = () => {
  useRedirectToNextStepOnceInitialized();

  return <FigureLogoAnimation />;
};

const RegisterRouter = () => {
  // Optimizely does not support FFs for non authed/identified users so this flag will need to be handled directly in the code :sadbeep:
  // TODO: remove the old flow once/if we prove the new flow is bug free and performs better.
  const isNewFlowEnabled = true;

  const pageRouteProps = {
    unsecured: true,
    isTwoColLayout: false,
    isCompleteApplication: false,
  };

  return (
    <Switch>
      {isNewFlowEnabled ? (
        <>
          <PageRoute exact path="/applications/start" {...pageRouteProps}>
            <RegisterRouterRedirectToCurrentStep />
          </PageRoute>
          <PageRoute path="/applications/start/:uuid/basic-info" {...pageRouteProps}>
            <BasicInfoAndCreateAccount />
          </PageRoute>
        </>
      ) : (
        <PageRoute exact path="/applications/start">
          <StartApplicationContainerOld />
        </PageRoute>
      )}
    </Switch>
  );
};

export default RegisterRouter;
